// Hook para gerenciamento de formulários: estado (values, errors, touched),
// validação por campo e do formulário completo, handlers de change/blur/submit.
// Usado no RegisterForm para gerenciar todo o cadastro.

import { useState, useCallback } from 'react';
import { validateRequired } from '../utils/validators';
import logger from '../utils/logger';

export const useForm = (initialState, validationRules = {}) => {
  const [values, setValues] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Cada regra é um objeto { required, message, validator }. `required` pode
  // ser um boolean ou uma função (values) => boolean, para obrigatoriedade condicional.
  const validateField = useCallback((name, value) => {
    const rules = validationRules[name];
    if (!rules) return null;

    for (const rule of rules) {
      const isRequired = typeof rule.required === 'function' ? rule.required(values) : rule.required;
      if (isRequired && !validateRequired(value)) {
        return rule.message || 'Campo obrigatório';
      }

      if (rule.validator) {
        const error = rule.validator(value, values);
        if (error) return error;
      }
    }
    return null;
  }, [validationRules, values]);

  const handleChange = useCallback((e) => {
    if (e?.target) {
      const { name, value } = e.target;

      setValues(prev => ({ ...prev, [name]: value }));

      // Só valida em tempo real se o campo já foi tocado
      if (touched[name]) {
        const error = validateField(name, value);
        setErrors(prev => ({ ...prev, [name]: error }));
      }
    }
  }, [touched, validateField]);

  const handleBlur = useCallback((e) => {
    const { name, value } = e.target;

    setTouched(prev => ({ ...prev, [name]: true }));

    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  }, [validateField]);

  /**
   * Define valor de um campo programaticamente. Útil para preenchimento
   * automático (ex: campos de endereço preenchidos após busca de CEP).
   */
  const setFieldValue = useCallback((name, value) => {
    setValues(prev => ({ ...prev, [name]: value }));

    if (touched[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  }, [touched, validateField]);

  const validateForm = useCallback(() => {
    const newErrors = {};
    let isValid = true;

    Object.keys(validationRules).forEach(field => {
      const error = validateField(field, values[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  }, [values, validationRules, validateField]);

  const handleSubmit = useCallback(async (onSubmit) => {
    setIsSubmitting(true);

    // Marca todos os campos como tocados para que os erros apareçam
    const allTouched = {};
    Object.keys(validationRules).forEach(field => {
      allTouched[field] = true;
    });
    setTouched(allTouched);

    const isValid = validateForm();

    if (isValid) {
      try {
        await onSubmit(values);
      } catch (error) {
        logger.error('Erro no submit:', error);
      }
    }

    setIsSubmitting(false);
  }, [values, validationRules, validateForm]);

  return {
    values,
    errors,
    touched,
    isSubmitting,

    handleChange,
    handleBlur,
    setFieldValue,
    handleSubmit,

    // Utilitários para controle mais fino (ex: setar erro vindo do backend)
    setValues,
    setErrors
  };
};
